/**
 * ProgressGallery — Obra en vivo redesigned as a visual gallery.
 *
 * Groups progress photos by canonical phase (foundation → structure → finishes → delivery),
 * cronological ascending. Opens a lightbox on click that lets the user navigate
 * the WHOLE filtered gallery cross-phase.
 *
 * Reuses GET /api/progress (supports optional ?villa_id=) and GET /api/villas.
 *
 * F-32: prefers `photo_files` (served via /api/files/{id} with cookie auth) and
 * falls back to legacy `photos_base64` so historical updates keep rendering.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Section } from "@/pages/Shell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { House, Camera, CircleNotch, Play, Trash, FileText, CheckCircle, ArrowRight, PencilSimple, X, CaretDown, CaretRight as CaretRightIcon } from "@phosphor-icons/react";
import LightboxViewer from "@/pages/investor/LightboxViewer";
import useInvestorLanguage from "@/i18n/useInvestorLanguage";

// Sentinel value used by ConstructionProgressPublisher's villa picker for
// "Todas las villas". Duplicated here (rather than imported) to avoid a
// circular module dependency between Publisher and this gallery.
const PROJECT_WIDE_VALUE = "__project_all__";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Only attach `crossOrigin="use-credentials"` when the backend really lives on
// a different origin from the frontend. Same-origin requests already send the
// auth cookie automatically and adding the attribute triggers an unnecessary
// CORS preflight that may fail in dev/proxy setups.
function isCrossOrigin() {
  if (!API_BASE) return false;
  try {
    return new URL(API_BASE).origin !== window.location.origin;
  } catch {
    return false;
  }
}
const CROSS_ORIGIN = isCrossOrigin();
const MEDIA_FALLBACK = "Este formato puede no reproducirse en este navegador. Prueba en Safari/iPhone o sube un MP4.";
const PHASE_ORDER = ["foundation", "structure", "finishes", "delivery"];
const PHASE_TKEY = {
  foundation: "progress.phase.foundation",
  structure:  "progress.phase.structure",
  finishes:   "progress.phase.finishes",
  delivery:   "progress.phase.delivery",
};

// Category filter chips (mirror backend _REPORT_CATEGORY_LIT). Chips with
// count == 0 are hidden at render time so the strip only surfaces categories
// the investor has real content for. "all" is always visible.
const CATEGORY_ORDER = [
  "general",
  "preparacion_terreno",
  "permisos",
  "cimentacion",
  "estructura",
  "cubierta",
  "acabados",
  "entrega",
  "otro",
];
const CATEGORY_TKEY = {
  general:             "progress.category.general",
  preparacion_terreno: "progress.category.preparacion_terreno",
  permisos:            "progress.category.permisos",
  cimentacion:         "progress.category.cimentacion",
  estructura:          "progress.category.estructura",
  cubierta:            "progress.category.cubierta",
  acabados:            "progress.category.acabados",
  entrega:             "progress.category.entrega",
  otro:                "progress.category.otro",
};
// Legacy villa-specific /progress rows carry a `phase` (4-phase enum) instead
// of `category`. Map them to the closest category so their photos count under
// the same chip as project-wide reports of the same stage.
const PHASE_TO_CATEGORY = {
  foundation: "cimentacion",
  structure:  "estructura",
  finishes:   "acabados",
  delivery:   "entrega",
};

// Sentinel bucket key for items without a resolvable `created_at`. Rendered
// as a trailing "Sin fecha" group so nothing gets silently dropped.
const NO_DATE_KEY = "__no-date__";
// Sentinel month key used to house every week whose `weekKey === NO_DATE_KEY`.
// Rendered as a trailing "Sin fecha" month, always after real months.
const NO_DATE_MONTH_KEY = "__no-date-month__";

// Extract "YYYY-MM" from a week key ("YYYY-MM-DD"). Returns the no-date
// sentinel when the week itself is undated so both concepts stay linked.
function monthKeyOf(weekKey) {
  if (!weekKey || weekKey === NO_DATE_KEY) return NO_DATE_MONTH_KEY;
  return weekKey.slice(0, 7);
}

// Localized "julio" / "July" for the accordion header. The year is displayed
// separately by the caller so it can be styled with less weight.
function formatMonthName(year, monthNum, locale) {
  try {
    return new Date(year, monthNum - 1, 1)
      .toLocaleDateString(locale, { month: "long" });
  } catch {
    return "";
  }
}

// Return the ISO date (YYYY-MM-DD) of the Monday that starts the local week
// containing `iso`. Uses the client's timezone on purpose — the timeline
// header matches how the investor perceives the calendar week. Returns null
// when the input can't be parsed.
function mondayOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // JS getDay(): Sunday=0..Saturday=6. Convert to Monday-start offset.
  const dow = d.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + offset);
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, "0");
  const day = String(monday.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Human range "1 – 7 jul" (same month) or "29 jun – 5 jul" (crosses months).
function formatWeekRange(mondayIso, locale) {
  if (!mondayIso) return "";
  const [y, m, d] = mondayIso.split("-").map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(y, m - 1, d + 6);
  const fmt = (dt, opts) => dt.toLocaleDateString(locale, opts);
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()} – ${end.getDate()} ${fmt(end, { month: "short" })}`;
  }
  return `${fmt(start, { day: "numeric", month: "short" })} – ${fmt(end, { day: "numeric", month: "short" })}`;
}

// Short date "1 jul" for week header title.
function formatWeekTitleDate(mondayIso, locale) {
  if (!mondayIso) return "";
  const [y, m, d] = mondayIso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(locale, { day: "numeric", month: "short" });
}


export default function ProgressGallery({ initialVillaId = null, hideVillaPicker = false } = {}) {
  const { t, lang } = useInvestorLanguage();
  const { user } = useAuth();
  // Gate the "Quitar" affordance on the *real* role, not the viewMode. An
  // admin previewing the investor view should still be able to remove media
  // by mistake-uploaded clips; an investor with viewMode=admin must not.
  const isAdmin = user?.role === "admin";
  const isConstructor = user?.role === "constructor";
  const canRemoveMedia = isAdmin || isConstructor;
  const dateLocale = lang === "en" ? "en-GB" : "es-ES";
  const phaseLabel = (ph) => (PHASE_TKEY[ph] && t(PHASE_TKEY[ph])) || ph;
  const [villas, setVillas] = useState([]);
  const [selectedId, setSelectedId] = useState(initialVillaId || null);
  const [rows, setRows] = useState([]);         // /progress rows (villa-specific)
  const [reports, setReports] = useState([]);   // /construction-reports rows (incl. project_all)
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightboxAt, setLightboxAt] = useState(null); // index into flatPhotos, or null
  const [editingReport, setEditingReport] = useState(null); // report object under edit, or null
  const [viewingReport, setViewingReport] = useState(null); // report object being viewed, or null
  const [editingPhoto, setEditingPhoto] = useState(null);   // media item under caption/date edit, or null

  const isProjectWideOnly = selectedId === PROJECT_WIDE_VALUE;

  // Load villas. If an explicit `initialVillaId` was provided, keep it as the
  // selected one even if it isn't in the fetched list (e.g. admin embedding
  // the gallery inside a villa-scoped tab).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/villas");
        setVillas(data);
        if (!initialVillaId && data.length > 0) {
          setSelectedId(data[0].villa_id);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [initialVillaId]);

  // Sync to a new initialVillaId if the parent changes it (admin switching villa).
  useEffect(() => {
    if (initialVillaId) setSelectedId(initialVillaId);
  }, [initialVillaId]);

  // Load progress + reports for selected villa. When `selectedId` is the
  // PROJECT_WIDE_VALUE sentinel (admin/constructor picked "Todas las villas"),
  // skip /progress entirely and load only project-wide reports.
  useEffect(() => {
    if (!selectedId) { setRows([]); setReports([]); return; }
    setLightboxAt(null);
    setFilter("all");
    loadRowsAndReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const loadRowsAndReports = async () => {
    if (!selectedId) return;
    const fetchProgress = isProjectWideOnly
      ? Promise.resolve({ data: [] })
      : api.get(`/progress?villa_id=${selectedId}`).catch((e) => { console.error(e); return { data: [] }; });
    // For a real villa, /construction-reports?villa_id=X returns reports
    // targeted at that villa AND any visibility_scope=project_all rows.
    // For the project-wide sentinel, omit the param to get everything visible.
    const reportParams = isProjectWideOnly ? {} : { villa_id: selectedId };
    const fetchReports = api.get("/construction-reports", { params: reportParams })
      .catch((e) => { console.error(e); return { data: [] }; });
    const [progRes, repRes] = await Promise.all([fetchProgress, fetchReports]);
    setRows(Array.isArray(progRes.data) ? progRes.data : []);
    setReports(Array.isArray(repRes.data) ? repRes.data : []);
  };

  const refreshRows = loadRowsAndReports;

  const removeMedia = async (fileId) => {
    if (!fileId || !canRemoveMedia) return;
    if (!window.confirm("¿Quitar este archivo? El inversor dejará de verlo.")) return;
    try {
      await api.delete(`/construction/uploads/${fileId}`);
      setLightboxAt(null);
      await refreshRows();
    } catch (e) {
      window.alert("No se pudo quitar el archivo. Intenta de nuevo.");
      console.error(e);
    }
  };

  // Flatten every media item from progress rows AND construction_reports into
  // a single list before applying filters/grouping. Report photos inherit
  // description from `summary || title` and progress row percentage from
  // the row itself. Ordering across sources is decided later by created_at.
  const allPhotos = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const ph = (r.phase || "").toLowerCase();
      // Legacy villa-specific progress rows: derive category from phase so
      // they count under the same chip as project-wide reports.
      const progressFilterKey = PHASE_TO_CATEGORY[ph] || "general";
      if (r.photo_files && r.photo_files.length > 0) {
        r.photo_files.forEach((pf) => {
          if (!pf || !pf.file_id) return;
          out.push({
            key: `${r.progress_id}-f-${pf.file_id}`,
            fileId: pf.file_id,
            ownerId: pf.owner_id || null,
            filename: pf.filename,
            src: `${API_BASE}/api/files/${pf.file_id}`,
            useCredentials: CROSS_ORIGIN,
            mime: pf.mime,
            isVideo: typeof pf.mime === "string" && pf.mime.startsWith("video/"),
            phase: ph,
            filterKey: progressFilterKey,
            percentage: r.percentage,
            date: pf.taken_at || r.created_at,
            taken_at: pf.taken_at || null,
            caption: pf.caption || "",
            description: pf.caption || r.description || "",
            source: "progress",
          });
        });
      } else {
        (r.photos_base64 || []).forEach((b64, i) => {
          const mime = b64.trimStart().startsWith("PHN2Zy") || b64.trimStart().startsWith("PD94")
            ? "image/svg+xml"
            : "image/jpeg";
          out.push({
            key: `${r.progress_id}-b-${i}`,
            fileId: null,
            filename: null,
            src: `data:${mime};base64,${b64}`,
            useCredentials: false,
            mime,
            isVideo: false,
            phase: ph,
            filterKey: progressFilterKey,
            percentage: r.percentage,
            date: r.created_at,
            description: r.description,
            source: "progress",
          });
        });
      }
    }
    for (const r of reports) {
      const reportKey = r.report_id || r.created_at || Math.random();
      // Report photos inherit their parent report's category so the whole
      // publication surfaces under one filter chip.
      const reportFilterKey = (r.category && CATEGORY_TKEY[r.category]) ? r.category : "general";
      (r.photo_files || []).forEach((pf) => {
        if (!pf || !pf.file_id) return;
        out.push({
          key: `${reportKey}-rf-${pf.file_id}`,
          fileId: pf.file_id,
          ownerId: pf.owner_id || null,
          filename: pf.filename,
          src: `${API_BASE}/api/files/${pf.file_id}`,
          useCredentials: CROSS_ORIGIN,
          mime: pf.mime,
          isVideo: typeof pf.mime === "string" && pf.mime.startsWith("video/"),
          // `phase` kept for backward compatibility (some downstream code
          // reads it); the filter now uses `filterKey`.
          phase: "report",
          filterKey: reportFilterKey,
          percentage: r.progress_pct ?? 0,
          date: pf.taken_at || r.created_at,
          taken_at: pf.taken_at || null,
          caption: pf.caption || "",
          description: pf.caption || r.summary || r.title || "",
          source: "report",
        });
      });
    }
    return out;
  }, [rows, reports]);

  // Weekly buckets keyed by Monday of the local week. Reports without photos
  // still create a bucket via their `created_at`. Items with no date go to
  // the trailing "Sin fecha" bucket. Order: most recent Monday first.
  const { weekGroups, flatPhotos } = useMemo(() => {
    // Apply the category filter to *media* only. Reports keep showing at
    // their week even when the current filter would hide the report's photos,
    // so the weekly reading stays intact (per approved decision).
    const filteredPhotos = filter === "all"
      ? allPhotos
      : allPhotos.filter((p) => p.filterKey === filter);

    const buckets = new Map();       // mondayIso → { weekKey, photos, reports }
    const ensure = (key) => {
      if (!buckets.has(key)) buckets.set(key, { weekKey: key, photos: [], reports: [] });
      return buckets.get(key);
    };

    for (const p of filteredPhotos) {
      const wk = mondayOf(p.date) || NO_DATE_KEY;
      ensure(wk).photos.push(p);
    }
    for (const r of reports) {
      const wk = mondayOf(r.created_at) || NO_DATE_KEY;
      ensure(wk).reports.push(r);
    }

    for (const b of buckets.values()) {
      b.photos.sort((a, x) => (a.date || "").localeCompare(x.date || ""));
      b.reports.sort((a, x) => (x.created_at || "").localeCompare(a.created_at || ""));
    }

    // Order: real weeks by Monday desc, "Sin fecha" always last.
    const sorted = [...buckets.values()].sort((a, b) => {
      if (a.weekKey === NO_DATE_KEY) return 1;
      if (b.weekKey === NO_DATE_KEY) return -1;
      return b.weekKey.localeCompare(a.weekKey);
    });

    // flatPhotos preserves the visual order the investor sees (top→bottom,
    // week header → photos), so lightbox indexes match the grid. Order is
    // deliberately anchored to the week sort — the month accordion below
    // reuses the same weeks in the same order so opening/closing months
    // never invalidates a `findIndex(p.key)` lookup.
    const flat = sorted.flatMap((b) => b.photos);
    return { weekGroups: sorted, flatPhotos: flat };
  }, [allPhotos, reports, filter]);

  // Bucket weeks under year+month accordions. Weeks stay in their existing
  // order inside each month so flatPhotos indices remain valid. The whole
  // memo depends on the already-filtered weekGroups, so category filters
  // propagate without extra work.
  const monthGroups = useMemo(() => {
    const buckets = new Map(); // monthKey → { monthKey, year, monthNum, weeks, totalFiles, totalReports }
    for (const wk of weekGroups) {
      const monthKey = monthKeyOf(wk.weekKey);
      let bucket = buckets.get(monthKey);
      if (!bucket) {
        let year = null;
        let monthNum = null;
        if (monthKey !== NO_DATE_MONTH_KEY) {
          const [y, m] = monthKey.split("-").map(Number);
          year = y;
          monthNum = m;
        }
        bucket = { monthKey, year, monthNum, weeks: [], totalFiles: 0, totalReports: 0 };
        buckets.set(monthKey, bucket);
      }
      bucket.weeks.push(wk);
      bucket.totalFiles += wk.photos.length;
      bucket.totalReports += wk.reports.length;
    }
    // Filter out months with zero activity in both dimensions (defensive —
    // weekGroups already omits empties, but keeps the API predictable).
    const visible = [...buckets.values()].filter(
      (m) => m.totalFiles > 0 || m.totalReports > 0,
    );
    // Order: real months desc by monthKey, no-date sentinel always last.
    visible.sort((a, b) => {
      if (a.monthKey === NO_DATE_MONTH_KEY) return 1;
      if (b.monthKey === NO_DATE_MONTH_KEY) return -1;
      return b.monthKey.localeCompare(a.monthKey);
    });
    return visible;
  }, [weekGroups]);

  // Accordion state. The most recent real month opens by default; the
  // no-date bucket stays collapsed unless it's the only group.
  const [openMonths, setOpenMonths] = useState(() => {
    const first = null;
    return new Set(first ? [first] : []);
  });

  useEffect(() => {
    setOpenMonths((prev) => {
      const validKeys = new Set(monthGroups.map((m) => m.monthKey));
      const next = new Set();
      // Preserve any month the user explicitly opened that still exists.
      for (const key of prev) {
        if (validKeys.has(key)) next.add(key);
      }
      // If nothing is open and we do have months, open the most recent one.
      if (next.size === 0 && monthGroups.length > 0) {
        const realFirst = monthGroups.find((m) => m.monthKey !== NO_DATE_MONTH_KEY);
        // Fall back to the no-date bucket if it's the only group present.
        const target = realFirst?.monthKey || monthGroups[0].monthKey;
        next.add(target);
      }
      return next;
    });
  }, [monthGroups]);

  const toggleMonth = (key) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const expandAllMonths = () => {
    setOpenMonths(new Set(monthGroups.map((m) => m.monthKey)));
  };
  const collapseAllMonths = () => setOpenMonths(new Set());

  // Week-level accordion. Only the first week of each month is open by
  // default (weeks inside a month are already ordered most-recent-first);
  // user toggles persist for the lifetime of the mount. This lets a busy
  // month scale without dumping every week's photos at once.
  const [openWeeks, setOpenWeeks] = useState(() => new Set());
  useEffect(() => {
    setOpenWeeks((prev) => {
      const validKeys = new Set();
      for (const m of monthGroups) for (const w of m.weeks) validKeys.add(w.weekKey);
      const next = new Set();
      for (const k of prev) if (validKeys.has(k)) next.add(k);
      // Seed defaults: first week of each month opens automatically the
      // first time it appears. Preserves any explicit toggle above.
      for (const m of monthGroups) {
        if (m.weeks.length === 0) continue;
        const first = m.weeks[0].weekKey;
        if (!prev.has(first) && ![...prev].some((k) => k === first)) next.add(first);
      }
      return next;
    });
  }, [monthGroups]);

  const toggleWeek = (key) => {
    setOpenWeeks((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Category counts for filter chips (always over unfiltered data). Any
  // filterKey we don't recognise falls back to "otro" so nothing is silently
  // dropped from the strip.
  const categoryCounts = useMemo(() => {
    const counts = { all: allPhotos.length };
    CATEGORY_ORDER.forEach((c) => { counts[c] = 0; });
    for (const p of allPhotos) {
      const key = counts[p.filterKey] != null ? p.filterKey : "otro";
      counts[key] += 1;
    }
    return counts;
  }, [allPhotos]);

  if (loading) {
    return (
      <Section title={t("progress.title")}>
        <div className="dv-card text-center py-16 text-stone-500 dark:text-stone-400">
          <CircleNotch size={32} className="mx-auto mb-3 animate-spin text-stone-400 dark:text-stone-500" />
          {t("progress.loading")}
        </div>
      </Section>
    );
  }

  if (villas.length === 0) {
    return (
      <Section title={t("progress.title")}>
        <EmptyState
          icon={House}
          title={t("progress.no_villa_title")}
          subtitle={t("progress.no_villa_subtitle")}
        />
      </Section>
    );
  }

  return (
    <div>
      {/* Villa selector — same pattern as MyProject / Performance.
          Hidden when the parent fixes the villa (admin/constructor embed). */}
      {!hideVillaPicker && villas.length >= 2 && (
        <div className="flex gap-2 flex-wrap mb-6" data-testid="gallery-villa-selector">
          {villas.map((v) => {
            const active = v.villa_id === selectedId;
            return (
              <button key={v.villa_id}
                data-testid={`gallery-villa-pill-${v.villa_id}`}
                onClick={() => setSelectedId(v.villa_id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active ? "bg-[#2F3E35] text-white shadow-sm" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-200 hover:bg-stone-200 dark:hover:bg-stone-700"
                }`}
              >
                <House size={14} weight="light" className="inline mr-1.5 -mt-0.5" />
                {v.name}
              </button>
            );
          })}
        </div>
      )}

      <Section
        title={t("progress.title")}
        subtitle={t("progress.subtitle")}
      >
        {weekGroups.length === 0 ? (
          <EmptyState
            icon={Camera}
            title={t("progress.no_photos_title")}
            subtitle={t("progress.no_photos_subtitle")}
          />
        ) : (
          <>
            {/* Category filter chips (secondary filter — timeline is the
                primary organizing axis). Only photos are filtered; reports
                stay pinned at their week. Empty categories are hidden so the
                strip never shows a chip with count 0 (except "Todas"). */}
            <div className="flex gap-2 flex-wrap mb-8" data-testid="gallery-phase-filter">
              <FilterChip
                label={t("progress.filter.all")}
                count={categoryCounts.all}
                active={filter === "all"}
                onClick={() => setFilter("all")}
                testid="gallery-filter-all"
              />
              {CATEGORY_ORDER.filter((c) => categoryCounts[c] > 0).map((c) => (
                <FilterChip
                  key={c}
                  label={t(CATEGORY_TKEY[c])}
                  count={categoryCounts[c]}
                  active={filter === c}
                  onClick={() => setFilter(c)}
                  testid={`gallery-filter-${c}`}
                />
              ))}
            </div>

            {/* Expand / collapse controls — only meaningful when there is
                more than one month to interact with. */}
            {monthGroups.length > 1 && (
              <div
                className="flex flex-wrap gap-2 mb-4 text-xs"
                data-testid="gallery-month-controls"
              >
                <button
                  type="button"
                  onClick={expandAllMonths}
                  className="px-2.5 py-1 rounded border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800"
                  data-testid="gallery-month-expand-all"
                >
                  {t("progress.month.expand_all")}
                </button>
                <button
                  type="button"
                  onClick={collapseAllMonths}
                  className="px-2.5 py-1 rounded border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800"
                  data-testid="gallery-month-collapse-all"
                >
                  {t("progress.month.collapse_all")}
                </button>
              </div>
            )}

            {/* Monthly accordion → weekly timeline */}
            <div className="space-y-4" data-testid="gallery-month-timeline">
              {monthGroups.map((month) => {
                const isMonthOpen = openMonths.has(month.monthKey);
                const isNoDateMonth = month.monthKey === NO_DATE_MONTH_KEY;
                const monthName = isNoDateMonth
                  ? t("progress.month.no_date")
                  : formatMonthName(month.year, month.monthNum, dateLocale);
                const headerLabel = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                const bodyId = `gallery-month-body-${month.monthKey}`;
                return (
                  <section
                    key={month.monthKey}
                    className="rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900"
                    data-testid={`gallery-month-${month.monthKey}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleMonth(month.monthKey)}
                      aria-expanded={isMonthOpen}
                      aria-controls={bodyId}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-h-[44px] hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                      data-testid={`gallery-month-toggle-${month.monthKey}`}
                    >
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="font-serif-dv text-lg sm:text-xl text-stone-900 dark:text-stone-100 truncate">
                          {headerLabel}
                        </span>
                        {!isNoDateMonth && (
                          <span className="text-sm text-stone-500 dark:text-stone-400">{month.year}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[11px] sm:text-xs text-stone-500 dark:text-stone-400 text-right" data-testid={`gallery-month-summary-${month.monthKey}`}>
                          {t("progress.month.summary", { weeks: month.weeks.length, files: month.totalFiles })}
                        </span>
                        {isMonthOpen
                          ? <CaretDown size={14} weight="bold" className="text-stone-500 dark:text-stone-400" />
                          : <CaretRightIcon size={14} weight="bold" className="text-stone-500 dark:text-stone-400" />}
                      </div>
                    </button>
                    {isMonthOpen && (
                      <div
                        id={bodyId}
                        className="px-4 pt-2 pb-6 border-t border-stone-100 dark:border-stone-800 space-y-12"
                      >
                        {month.weeks.map((group) => {
                          const isNoDate = group.weekKey === NO_DATE_KEY;
                const titleDate = isNoDate ? null : formatWeekTitleDate(group.weekKey, dateLocale);
                const range = isNoDate ? "" : formatWeekRange(group.weekKey, dateLocale);
                const isWeekOpen = openWeeks.has(group.weekKey);
                const weekBodyId = `gallery-week-body-${group.weekKey}`;
                return (
                  <div key={group.weekKey} data-testid={`gallery-week-${group.weekKey}`}>
                    {/* Week header — now a toggle so busy months don't render
                        every week's photos at once. Chevron mirrors the month
                        accordion pattern. */}
                    <button
                      type="button"
                      onClick={() => toggleWeek(group.weekKey)}
                      aria-expanded={isWeekOpen}
                      aria-controls={weekBodyId}
                      className="w-full flex items-end justify-between mb-4 pb-3 border-b border-stone-200 dark:border-stone-700 text-left hover:border-stone-400 dark:hover:border-stone-500 transition-colors"
                      data-testid={`gallery-week-toggle-${group.weekKey}`}
                    >
                      <div>
                        <div className="text-xs tracking-[0.2em] uppercase text-stone-500 dark:text-stone-400 mb-0.5">
                          {t("progress.phase_label")}
                        </div>
                        <div className="font-serif-dv text-2xl">
                          {isNoDate
                            ? t("progress.no_date_group")
                            : t("progress.week_of", { date: titleDate })}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {!isNoDate && (
                          <div className="text-xs text-stone-500 dark:text-stone-400 text-right">
                            {t("progress.week_range_files", {
                              range,
                              files: group.photos.length,
                              reports: group.reports.length,
                            })}
                          </div>
                        )}
                        {isWeekOpen
                          ? <CaretDown size={14} weight="bold" className="text-stone-500 dark:text-stone-400 shrink-0" />
                          : <CaretRightIcon size={14} weight="bold" className="text-stone-500 dark:text-stone-400 shrink-0" />}
                      </div>
                    </button>

                    {isWeekOpen && (<div id={weekBodyId}>
                    {/* Weekly summary — one card per week even if the admin
                        posted several construction_reports in the same week.
                        The most recent report (by created_at) is the source
                        of truth for summary + milestones + progress; older
                        reports contribute only their media count and are
                        acknowledged via a small footer. */}
                    {group.reports.length > 0 && (() => {
                      const sortedDesc = [...group.reports].sort(
                        (a, b) => (b.created_at || "").localeCompare(a.created_at || "")
                      );
                      const canonical = sortedDesc[0];
                      const extraCount = group.reports.length - 1;
                      const weeklyMediaCount = group.reports.reduce(
                        (acc, r) => acc + ((r.photo_files || []).length), 0
                      );
                      return (
                        <div className="mb-5">
                          <WeeklyReportCard
                            report={canonical}
                            extraCount={extraCount}
                            weeklyMediaCount={weeklyMediaCount}
                            dateLocale={dateLocale}
                            t={t}
                            canEdit={isAdmin || isConstructor}
                            onEdit={() => setEditingReport(canonical)}
                            onView={() => setViewingReport(canonical)}
                          />
                        </div>
                      );
                    })()}

                    {/* Photo grid */}
                    {group.photos.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {group.photos.map((photo) => {
                          const flatIdx = flatPhotos.findIndex((p) => p.key === photo.key);
                          // Admin can remove/edit anything; constructor can only
                          // remove/edit files they uploaded themselves (matches
                          // backend rule for DELETE/PATCH /construction/uploads/{id}).
                          const ownsFile = !!photo.ownerId && photo.ownerId === user?.user_id;
                          const showRemove = !!photo.fileId && (isAdmin || (isConstructor && ownsFile));
                          const showEdit = !!photo.fileId && (isAdmin || (isConstructor && ownsFile));
                          return (
                            <PhotoThumb
                              key={photo.key}
                              photo={photo}
                              onOpen={() => setLightboxAt(flatIdx)}
                              onRemove={showRemove ? () => removeMedia(photo.fileId) : null}
                              onEdit={showEdit ? () => setEditingPhoto(photo) : null}
                              dateLocale={dateLocale}
                            />
                          );
                        })}
                      </div>
                    )}
                    </div>)}
                  </div>
                );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>{/* /gallery-month-timeline */}
          </>
        )}
      </Section>

      {lightboxAt != null && flatPhotos.length > 0 && (
        <LightboxViewer
          photos={flatPhotos}
          initialIndex={lightboxAt}
          onClose={() => setLightboxAt(null)}
          onRemove={canRemoveMedia ? removeMedia : null}
          canRemovePhoto={canRemoveMedia ? (p) => (
            !!p?.fileId && (isAdmin || (isConstructor && p.ownerId === user?.user_id))
          ) : null}
        />
      )}

      {editingReport && (
        <ReportEditModal
          report={editingReport}
          onClose={() => setEditingReport(null)}
          onSaved={async () => {
            setEditingReport(null);
            await refreshRows();
          }}
        />
      )}

      {editingPhoto && (
        <MediaEditModal
          photo={editingPhoto}
          onClose={() => setEditingPhoto(null)}
          onSaved={async () => {
            setEditingPhoto(null);
            await refreshRows();
          }}
        />
      )}

      {viewingReport && (
        <ReportViewModal
          report={viewingReport}
          onClose={() => setViewingReport(null)}
          onOpenPhoto={(fileId) => {
            const idx = flatPhotos.findIndex((p) => p.fileId === fileId);
            if (idx >= 0) {
              setViewingReport(null);
              setLightboxAt(idx);
            }
          }}
          dateLocale={dateLocale}
          t={t}
        />
      )}
    </div>
  );
}


function FilterChip({ label, count, active, disabled, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
        active
          ? "bg-[#2F3E35] text-white shadow-sm"
          : disabled
          ? "bg-stone-50 dark:bg-stone-800 text-stone-300 cursor-not-allowed"
          : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-200 hover:bg-stone-200 dark:hover:bg-stone-700"
      }`}
    >
      {label} <span className="opacity-60 text-xs ml-1">· {count}</span>
    </button>
  );
}


function PhotoThumb({ photo, onOpen, onRemove, onEdit, dateLocale = "es-ES" }) {
  const [failed, setFailed] = useState(false);
  const shortDate = photo.date
    ? new Date(photo.date).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })
    : "";

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onRemove?.();
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onEdit?.();
  };

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onOpen}
        data-testid={`gallery-thumb-${photo.key}`}
        className="block w-full text-left transition-transform hover:-translate-y-0.5"
      >
        <div
          className="relative rounded-md overflow-hidden bg-stone-100 dark:bg-stone-800"
          style={{ aspectRatio: "4/3" }}
        >
          {failed ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-stone-500 dark:text-stone-400 px-2 text-center gap-1">
              <Camera size={24} weight="light" />
              {photo.isVideo && (
                <span className="text-[10px] leading-tight">{MEDIA_FALLBACK}</span>
              )}
            </div>
          ) : photo.isVideo ? (
            <>
              <video
                src={photo.src}
                muted
                preload="metadata"
                playsInline
                crossOrigin={photo.useCredentials ? "use-credentials" : undefined}
                onError={() => setFailed(true)}
                className="w-full h-full object-cover bg-black"
              />
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="w-10 h-10 rounded-full bg-black/55 text-white flex items-center justify-center">
                  <Play size={18} weight="fill" />
                </span>
              </span>
              <span className="absolute bottom-1 left-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-black/55 text-white">
                Vídeo
              </span>
            </>
          ) : (
            <img
              src={photo.src}
              alt=""
              loading="lazy"
              crossOrigin={photo.useCredentials ? "use-credentials" : undefined}
              onError={() => setFailed(true)}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
          )}
        </div>
        <div className="mt-1.5 text-[11px] text-stone-500 dark:text-stone-400">{shortDate}</div>
        {photo.description && (
          <div
            className="mt-0.5 text-xs text-stone-600 dark:text-stone-300 line-clamp-2"
            data-testid={`gallery-thumb-desc-${photo.key}`}
          >
            {photo.description}
          </div>
        )}
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={handleRemoveClick}
          aria-label="Quitar archivo"
          title="Quitar archivo"
          data-testid={`gallery-remove-${photo.key}`}
          className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          <Trash size={14} weight="bold" />
        </button>
      )}
      {onEdit && photo.fileId && (
        <button
          type="button"
          onClick={handleEditClick}
          aria-label="Editar archivo"
          title="Editar descripción y fecha"
          data-testid={`media-edit-${photo.fileId}`}
          className={`absolute ${onRemove ? "top-1.5 right-11" : "top-1.5 right-1.5"} w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`}
        >
          <PencilSimple size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}


// One "Resumen semanal" card per week. `report` is the canonical (most
// recent) construction_report of the week; summary/milestones/progress come
// from it. `weeklyMediaCount` is the sum of photo_files across every report
// of the week (may exceed report.photo_files.length when multiple reports
// share the week). `extraCount` is how many additional reports exist in the
// same week beyond the canonical one — surfaced as a discreet footnote so
// the investor knows there was more activity without piling up cards.
function WeeklyReportCard({ report, extraCount = 0, weeklyMediaCount = 0, dateLocale, t, canEdit = false, onEdit, onView }) {
  const shortDate = report.created_at
    ? new Date(report.created_at).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })
    : "";
  return (
    <div
      className="dv-card border-l-4"
      style={{ borderLeftColor: "#9c5236" }}
      data-testid={`weekly-report-${report.report_id || report.created_at}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: "#fbf3ee" }}>
          <FileText size={18} weight="light" style={{ color: "#9c5236" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 dark:text-stone-400 mb-0.5">
              {t("progress.weekly_summary_kicker")}
              {shortDate ? ` · ${shortDate}` : ""}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {onView && report.report_id && (
                <button
                  type="button"
                  onClick={onView}
                  data-testid={`weekly-report-view-${report.report_id}`}
                  className="text-[11px] px-2 py-1 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-900 dark:hover:border-stone-50 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-1"
                >
                  {t("progress.view_report")}
                </button>
              )}
              {canEdit && onEdit && report.report_id && (
                <button
                  type="button"
                  onClick={onEdit}
                  data-testid={`weekly-report-edit-${report.report_id}`}
                  className="text-[11px] px-2 py-1 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-900 dark:hover:border-stone-50 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-1"
                >
                  <PencilSimple size={12} /> Editar
                </button>
              )}
            </div>
          </div>
          {report.summary && (
            <p className="text-sm text-stone-700 dark:text-stone-200 mt-1 whitespace-pre-line">{report.summary}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-600 dark:text-stone-300">
            {report.reporting_period && (
              <div>
                <span className="text-stone-400 dark:text-stone-500">{t("progress.report_period")}: </span>
                {report.reporting_period}
              </div>
            )}
            {weeklyMediaCount > 0 && (
              <div>{t("progress.report_media_count", { n: weeklyMediaCount })}</div>
            )}
          </div>
          {(report.completed_milestone || report.next_milestone) && (
            <div className="mt-3 grid gap-1 text-xs">
              {report.completed_milestone && (
                <div className="flex items-start gap-1.5">
                  <CheckCircle size={12} weight="fill" className="mt-0.5 shrink-0" style={{ color: "#3F7D58" }} />
                  <span>
                    <span className="text-stone-400 dark:text-stone-500">{t("progress.report_completed")}: </span>
                    {report.completed_milestone}
                  </span>
                </div>
              )}
              {report.next_milestone && (
                <div className="flex items-start gap-1.5">
                  <ArrowRight size={12} weight="bold" className="mt-0.5 shrink-0 text-stone-400 dark:text-stone-500" />
                  <span>
                    <span className="text-stone-400 dark:text-stone-500">{t("progress.report_next")}: </span>
                    {report.next_milestone}
                  </span>
                </div>
              )}
            </div>
          )}
          {extraCount > 0 && (
            <div
              className="mt-3 pt-2 border-t border-stone-100 dark:border-stone-800 text-[11px] text-stone-400 dark:text-stone-500"
              data-testid="weekly-extra-reports"
            >
              {t("progress.weekly_extra_reports", { n: extraCount })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="dv-card text-center py-16" data-testid="gallery-empty">
      <Icon size={40} weight="light" className="mx-auto mb-3 text-stone-400 dark:text-stone-500" />
      <div className="font-serif-dv text-2xl mb-2">{title}</div>
      <div className="text-stone-500 dark:text-stone-400 text-sm max-w-md mx-auto">{subtitle}</div>
    </div>
  );
}


// V1 edit modal — metadata only, no attachment changes. Attachments (photos,
// Read-only viewer for the weekly report. Visible to every role (investor
// included). Thumbnails render `photo_files` and delegate to the parent for
// opening the shared lightbox at the right index.
function ReportViewModal({ report, onClose, onOpenPhoto, dateLocale, t }) {
  const shortDate = report.created_at
    ? new Date(report.created_at).toLocaleDateString(dateLocale, {
        day: "numeric", month: "long", year: "numeric",
      })
    : "";
  const files = Array.isArray(report.photo_files) ? report.photo_files : [];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto"
      onClick={onClose}
      data-testid="report-view-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="dv-card max-w-2xl w-full bg-white dark:bg-stone-900 my-8 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 dark:text-stone-400 mb-1">
              {t("progress.report_view_modal_title")}
              {shortDate ? ` · ${shortDate}` : ""}
            </div>
            {report.title && (
              <div className="font-serif-dv text-2xl">{report.title}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("progress.close")}
            data-testid="report-view-modal-close"
            className="w-9 h-9 rounded-full bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-200 flex items-center justify-center shrink-0"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {report.summary && (
          <p className="text-sm text-stone-700 dark:text-stone-200 whitespace-pre-line">
            {report.summary}
          </p>
        )}

        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-600 dark:text-stone-300">
          {report.reporting_period && (
            <div>
              <span className="text-stone-400 dark:text-stone-500">{t("progress.report_period")}: </span>
              {report.reporting_period}
            </div>
          )}
          {files.length > 0 && (
            <div>{t("progress.report_media_count", { n: files.length })}</div>
          )}
        </div>

        {(report.completed_milestone || report.next_milestone) && (
          <div className="grid gap-1 text-xs">
            {report.completed_milestone && (
              <div className="flex items-start gap-1.5">
                <CheckCircle size={12} weight="fill" className="mt-0.5 shrink-0" style={{ color: "#3F7D58" }} />
                <span>
                  <span className="text-stone-400 dark:text-stone-500">{t("progress.report_completed")}: </span>
                  {report.completed_milestone}
                </span>
              </div>
            )}
            {report.next_milestone && (
              <div className="flex items-start gap-1.5">
                <ArrowRight size={12} weight="bold" className="mt-0.5 shrink-0 text-stone-400 dark:text-stone-500" />
                <span>
                  <span className="text-stone-400 dark:text-stone-500">{t("progress.report_next")}: </span>
                  {report.next_milestone}
                </span>
              </div>
            )}
          </div>
        )}

        {files.length > 0 && (
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 dark:text-stone-400 mb-2">
              {t("progress.report_photos_heading")}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {files.map((pf) => {
                const isVideo = typeof pf.mime === "string" && pf.mime.startsWith("video/");
                return (
                  <button
                    key={pf.file_id}
                    type="button"
                    onClick={() => onOpenPhoto?.(pf.file_id)}
                    data-testid={`report-view-thumb-${pf.file_id}`}
                    className="relative aspect-square rounded overflow-hidden bg-stone-100 dark:bg-stone-800 hover:opacity-90 transition"
                  >
                    {isVideo ? (
                      <div className="w-full h-full flex items-center justify-center text-stone-500 dark:text-stone-400">
                        <Play size={22} weight="fill" />
                      </div>
                    ) : (
                      <img
                        src={`${API_BASE}/api/files/${pf.file_id}`}
                        alt=""
                        crossOrigin={CROSS_ORIGIN ? "use-credentials" : undefined}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// videos) stay untouched and are managed by their own delete/upload flow.
function ReportEditModal({ report, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: report.title || "",
    summary: report.summary || "",
    reporting_period: report.reporting_period || "",
    completed_milestone: report.completed_milestone || "",
    next_milestone: report.next_milestone || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setErr("El título es obligatorio.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.patch(`/construction-reports/${report.report_id}`, {
        title: form.title.trim(),
        summary: form.summary,
        reporting_period: form.reporting_period || null,
        completed_milestone: form.completed_milestone || null,
        next_milestone: form.next_milestone || null,
      });
      await onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      data-testid="report-edit-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="dv-card max-w-lg w-full bg-white dark:bg-stone-900 space-y-3"
      >
        <div className="flex items-center justify-between">
          <div className="font-serif-dv text-xl">Editar reporte semanal</div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 dark:text-stone-500 hover:text-stone-700 dark:hover:text-stone-200"
            aria-label="Cerrar"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">Título</label>
          <input
            type="text"
            className="dv-input mt-1"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            maxLength={200}
            data-testid="report-edit-title"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">Resumen</label>
          <textarea
            className="dv-input mt-1"
            rows={4}
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            maxLength={8000}
            data-testid="report-edit-summary"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">Período</label>
          <input
            type="text"
            className="dv-input mt-1"
            value={form.reporting_period}
            onChange={(e) => setForm({ ...form, reporting_period: e.target.value })}
            maxLength={60}
            placeholder="ej. Semana del 10 al 16 de junio"
            data-testid="report-edit-period"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">Hito completado</label>
            <input
              type="text"
              className="dv-input mt-1"
              value={form.completed_milestone}
              onChange={(e) => setForm({ ...form, completed_milestone: e.target.value })}
              data-testid="report-edit-completed"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">Próximo hito</label>
            <input
              type="text"
              className="dv-input mt-1"
              value={form.next_milestone}
              onChange={(e) => setForm({ ...form, next_milestone: e.target.value })}
              data-testid="report-edit-next"
            />
          </div>
        </div>
        {err && (
          <div className="text-sm px-3 py-2 rounded-md border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-300">
            {err}
          </div>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="dv-btn dv-btn-ghost"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="dv-btn dv-btn-primary"
            data-testid="report-edit-submit"
          >
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}


// Edit modal for a single construction upload (photo or video). Only edits
// caption + taken_at via PATCH /construction/uploads/{id}. Everything else
// is intentionally read-only in V1.
function MediaEditModal({ photo, onClose, onSaved }) {
  const [caption, setCaption] = useState(photo?.caption || photo?.description || "");
  const [takenAt, setTakenAt] = useState(photo?.taken_at || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!photo?.fileId) return;
    setErr("");
    setBusy(true);
    try {
      await api.patch(`/construction/uploads/${photo.fileId}`, {
        caption: caption.trim() || null,
        taken_at: takenAt || null,
      });
      await onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "No se pudo guardar. Intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-edit-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-stone-900 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 border border-transparent dark:border-stone-700"
      >
        <div className="flex items-center justify-between">
          <div className="font-serif-dv text-xl">Editar avance</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-stone-400 dark:text-stone-500 hover:text-stone-700 dark:hover:text-stone-200"
            data-testid="media-edit-cancel"
          >
            <X size={20} />
          </button>
        </div>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1">Descripción</div>
          <textarea
            className="dv-input w-full"
            rows={3}
            maxLength={500}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Ej. Cimentación terminada, próximo hito estructura."
            data-testid="media-edit-caption"
          />
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1">Fecha del avance</div>
          <input
            type="date"
            className="dv-input w-full"
            value={takenAt}
            onChange={(e) => setTakenAt(e.target.value)}
            data-testid="media-edit-taken-at"
          />
          <div className="text-[11px] text-stone-500 dark:text-stone-400 mt-1">
            Si dejas vacío se usa la fecha de subida.
          </div>
        </label>
        {err && (
          <div
            className="text-xs px-2 py-1.5 rounded border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-300"
            data-testid="media-edit-error"
          >
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="dv-btn dv-btn-ghost"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="dv-btn dv-btn-primary"
            data-testid="media-edit-save"
          >
            {busy ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </div>
  );
}
